import React from "react";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import Loader from "components/Loader";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

// Material Dashboard 2 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// redux
import { useSelector, useDispatch } from "react-redux";
import * as actions from "redux/actions/behavioural";

const emptyForm = {
  id: null,
  sleepScore: "",
  foodScore: "",
  moodScore: "",
  activityScore: "",
};

const SCORE_FIELDS = [
  { name: "sleepScore", label: "Sleep" },
  { name: "foodScore", label: "Food" },
  { name: "moodScore", label: "Mood" },
  { name: "activityScore", label: "Activity" },
];

const BehaviouralForm = () => {
  const dispatch = useDispatch();

  const behavioural = useSelector((state) => state.behavioural);

  const [form, setForm] = React.useState(emptyForm);

  // Load today's record, if one already exists, on mount.
  React.useEffect(() => {
    dispatch(actions.getBehavioural());
  }, [dispatch]);

  // Prefill the form once the record loads (or reset to empty if none exists).
  React.useEffect(() => {
    if (behavioural.record) {
      setForm({
        id: behavioural.record.id,
        sleepScore: behavioural.record.sleepScore ?? "",
        foodScore: behavioural.record.foodScore ?? "",
        moodScore: behavioural.record.moodScore ?? "",
        activityScore: behavioural.record.activityScore ?? "",
      });
    }
  }, [behavioural.record]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = () => {
    dispatch(actions.saveBehavioural(form));
  };

  const isUpdate = Boolean(form.id);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Grid container>
        {behavioural.loading ? (
          <Loader />
        ) : (
          <Grid item xs={12} md={8} lg={6}>
            <MDBox py={3} pr={1}>
              <Card sx={{ width: "100%" }}>
                <MDBox display="flex">
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    width="4rem"
                    height="4rem"
                    variant="gradient"
                    bgColor="primary"
                    color="white"
                    shadow="md"
                    borderRadius="xl"
                    ml={3}
                    mt={-2}
                  >
                    <Icon fontSize="medium" color="inherit">
                      watch
                    </Icon>
                  </MDBox>
                  <MDBox>
                    <MDTypography variant="h6" sx={{ mt: 2, mb: 1, ml: 2 }}>
                      {isUpdate ? "Update Today's Behavioural Score" : "Today's Behavioural Score"}
                    </MDTypography>
                  </MDBox>
                </MDBox>

                <MDBox p={3}>
                  <MDTypography variant="button" color="text" sx={{ display: "block", mb: 3 }}>
                    Rate today on a scale of 0–10 for each area. Saving again today updates this
                    same record — it doesn't create a new one.
                  </MDTypography>

                  <Grid container spacing={3}>
                    {SCORE_FIELDS.map(({ name, label }) => (
                      <Grid item xs={12} sm={6} key={name}>
                        <MDInput
                          type="number"
                          label={label}
                          name={name}
                          value={form[name]}
                          onChange={handleChange}
                          inputProps={{ min: 0, max: 10, step: 0.5 }}
                          fullWidth
                        />
                      </Grid>
                    ))}
                  </Grid>

                  <MDBox mt={4} mb={1}>
                    <MDButton
                      variant="gradient"
                      color="primary"
                      onClick={handleSubmit}
                      disabled={behavioural.savingBehavioural}
                    >
                      {behavioural.savingBehavioural
                        ? "Saving..."
                        : isUpdate
                        ? "Update"
                        : "Save"}
                    </MDButton>
                  </MDBox>
                </MDBox>
              </Card>
            </MDBox>
          </Grid>
        )}
      </Grid>
    </DashboardLayout>
  );
};

export default BehaviouralForm;
